import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock as LockIcon, MessageSquare, User } from 'lucide-react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api-client';
import { avatarGradientClass } from '@/lib/avatar-gradient';
import { formatPhone } from '@/lib/format-phone';
import { ConversationAccountBanner } from '@/features/accounts/ConversationAccountBanner';
import { useAccounts } from '@/features/accounts/useAccounts';
import { Composer } from '@/features/chat/Composer';
import { ProfileSidebar } from '@/features/chat/ProfileSidebar';
import { shouldIgnoreConversationEscape } from '@/features/chat/escape-guard';
import { MessageList } from '@/features/chat/MessageList';
import { ThreadPanel } from '@/features/chat/ThreadPanel';
import {
  conversationOutSchema,
  type Conversation,
  type Lock,
} from '@/features/conversations/conversations.api';
import { CONVERSATIONS_ROOT, conversationsKeys } from '@/features/conversations/keys';
import { useAuthStore } from '@/store/auth';
import { usePrefsStore } from '@/store/prefs';
import { useUIStore } from '@/store/ui';
import { cn } from '@/lib/utils';

import type { Message } from '@/features/chat/chat.api';

/**
 * Right column of the console shell — the chat panel for the currently
 * selected conversation.
 *
 *   - Header strip: contact phone (primary) + display name (secondary, when
 *     both exist) + channel badge + lock pill. Lock pill state is read from
 *     `['conversations', 'lock', id]` so it tracks the claim/release
 *     mutations and (M5-T6) the WebSocket lock events without duplicating
 *     logic.
 *   - `<ConversationAccountBanner>` (2026-09 disconnect early warning, spec
 *     `docs/specs/2026-09-features/01-disconnect-early-warning.md` §3.5):
 *     between the header and the list, renders only when this conversation's
 *     OWN account is not `connected` — "此账号当前无法发送". Resolves the
 *     account from the cache-warm `useAccounts()` (already kept live by the
 *     WS `account.status` projection), so no extra fetch.
 *   - Body: `<MessageList>` (virtualized, reverse-scroll history).
 *   - Footer: `<Composer>` (text + attachment, optimistic outbound).
 *   - Empty state when nothing is selected.
 *
 * The conversation detail (for the header's phone/display name) is fetched
 * with the `['conversations', 'detail', id]` key — the same key the
 * conversation list shares, so the cache is warm if the row was
 * already in the list. Falls back to a quiet skeleton otherwise.
 */
export function ChatPanel(): React.JSX.Element {
  const selectedConversationId = useUIStore((s) => s.selectedConversationId);
  const selectedThread = useUIStore((s) => s.selectedThread);
  // Customer-profile sidebar (the 4th column) lives INSIDE this panel as a
  // flex sibling of the chat column, so the outer 3-panel resizable
  // persistence in Layout is untouched. The merged-thread branch wires the
  // SAME sidebar itself (ThreadPanel owns its column wrapper + sidebar
  // siblings, because only it knows the currently-chosen member conversation
  // the sidebar must key on).
  const profileOpen = usePrefsStore((s) => s.profileOpen);
  const profileSide = usePrefsStore((s) => s.profileSide);

  // The section is a flex ROW: [profile?] · chat column · [profile?]. Each
  // branch's content keeps its own `flex flex-1 flex-col min-h-0 min-w-0`
  // wrapper so the header/list/composer stack + internal scroll chain behave
  // exactly as they did when they were direct flex-col children of the section.
  //
  // The two selections are mutually exclusive (the store's setters enforce
  // it), so this is a simple three-way gate: merged thread wins when set,
  // then the single conversation, then the empty state.
  return (
    <section className="neu-panel-chat relative z-0 flex h-full min-h-0">
      {selectedThread ? (
        <ThreadPanel thread={selectedThread} />
      ) : selectedConversationId ? (
        <>
          {profileOpen && profileSide === 'left' && (
            <ProfileSidebar conversationId={selectedConversationId} />
          )}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <ChatPanelInner conversationId={selectedConversationId} />
          </div>
          {profileOpen && profileSide === 'right' && (
            <ProfileSidebar conversationId={selectedConversationId} />
          )}
        </>
      ) : (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <EmptyNoConversation />
        </div>
      )}
    </section>
  );
}

interface ChatPanelInnerProps {
  conversationId: string;
}

function ChatPanelInner({ conversationId }: ChatPanelInnerProps): React.JSX.Element {
  const setSelectedConversationId = useUIStore((s) => s.setSelectedConversationId);

  // The message currently being replied to — lifted here (rather than into
  // a global store) so it flows bubble → ChatPanelInner → Composer through
  // plain props. `null` means "no reply in progress" (the normal case).
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);

  // Reset on conversation switch. `ChatPanelInner` is NOT remounted when
  // `selectedConversationId` changes to a different conversation (no `key`
  // prop on it in `ChatPanel`), so a reply banner left over from the
  // previous chat would otherwise bleed into the newly opened one.
  useEffect(() => {
    setReplyingTo(null);
  }, [conversationId]);

  // ESC-to-close: pressing Escape returns to the empty state, EXCEPT while
  // the composer's textarea is focused — there, the Composer's own
  // `onKeyDown` handles Escape locally (clear the reply banner / attachment
  // chip, then blur the textarea) and calls `stopPropagation()` so this
  // document-level listener never sees that keystroke. Once the textarea is
  // blurred, the *next* Escape press targets `document.body` (or whatever
  // has focus) and bubbles straight here — that's the "second press" this
  // handler answers. A first Escape pressed while focus is already outside
  // the composer (e.g. nothing focused, or the header) closes immediately.
  //
  // The actual "should this Escape be ignored" decision lives in
  // `shouldIgnoreConversationEscape` (./escape-guard.ts) — see its doc
  // comment for the image-lightbox bug this guards against.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      if (shouldIgnoreConversationEscape(e)) return;
      setSelectedConversationId(null);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [setSelectedConversationId]);

  const conversationQuery = useQuery<Conversation, Error>({
    queryKey: conversationsKeys.detail(conversationId),
    queryFn: async () => {
      const response = await apiClient.get(`/conversations/${conversationId}`);
      return conversationOutSchema.parse(response.data);
    },
    staleTime: 30_000,
  });

  const lockQuery = useQuery<Lock | null, Error>({
    queryKey: conversationsKeys.lock(conversationId),
    // No standalone GET endpoint; rely on cache populated by the row's
    // own probe + claim/release mutations + (M5-T6) WebSocket events.
    // `enabled: false` keeps this from firing a queryFn; we just read.
    queryFn: () => Promise.resolve(null),
    enabled: false,
  });

  // Mirror the pattern used by AccountRow's `useAccountUnreadSum`: subscribe
  // to QueryCache notifications so that `removeQueries` (issued by the WS
  // projection on a lock release) forces a re-render. Without this,
  // `useQuery({ enabled: false })` only re-renders when `setQueryData` writes
  // to the slot — it does NOT react to `removeQueries` dropping the slot.
  // `ConversationRow` benefits from the same re-render incidentally via its
  // shared cache subscription; the header has no such piggyback so we wire it
  // explicitly. We do NOT flip `enabled: true` — that would re-introduce the
  // network probe deleted in M5-T6 and break the cache-only design contract.
  //
  // The subscription MUST be scoped, in two dimensions (see the regression
  // suite in ChatPanel.test.tsx):
  //
  //   - EVENT TYPE — only `updated` / `removed` can change the lock slot this
  //     panel reads. Crucially this excludes `observerOptionsUpdated`, which
  //     this component EMITS on every own re-render: `useBaseQuery` re-runs
  //     `observer.setOptions` in an effect after each render, and the inline
  //     `queryFn`s above make the options never shallow-equal. An unscoped
  //     callback therefore closed a self-sustaining cycle — any one cache
  //     event anywhere → setLockTick → re-render → observerOptionsUpdated ×2
  //     → setLockTick → … — an unbounded render loop (silent CPU burn in the
  //     browser, a hard `act()` hang under RTL). It also excludes `added`,
  //     which react-query fires synchronously INSIDE the render of whichever
  //     component first mounts a new key — reacting to it would setState this
  //     panel mid-render of that component (React's "Cannot update a
  //     component while rendering a different component" dev warning).
  //   - QUERY KEY — only the `conversations` root feeds anything this panel
  //     derives. The scope is deliberately the whole root (not just this
  //     conversation's lock key): it keeps the incidental behavior the panel
  //     has always relied on — any conversations-side traffic (e.g. the 30s
  //     list poll) re-evaluates the header's `Date.now()` lock-expiry check.
  const queryClient = useQueryClient();
  const [, setLockTick] = useState(0);
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' && event.type !== 'removed') return;
      if (event.query.queryKey[0] !== CONVERSATIONS_ROOT) return;
      setLockTick((t) => t + 1);
    });
    return unsubscribe;
  }, [queryClient]);

  // Re-read the lock slot from live cache each render (tick drives re-render;
  // this read picks up the post-`removeQueries` absence of data).
  const liveLock =
    queryClient.getQueryData<Lock | null>(conversationsKeys.lock(conversationId)) ??
    lockQuery.data ??
    null;

  const currentAgent = useAuthStore((s) => s.agent);
  const conversation = conversationQuery.data;
  const lock = liveLock ?? undefined;
  const isExpired = lock ? new Date(lock.expires_at).getTime() < Date.now() : false;
  const effectiveLock = lock && !isExpired ? lock : undefined;
  const isLockedBySelf =
    effectiveLock && currentAgent
      ? effectiveLock.locked_by_agent_id === currentAgent.id
      : false;

  // The conversation's OWN account — for `ConversationAccountBanner` (spec 01
  // §3.5). `useAccounts()` shares the app-wide `['accounts']` cache (already
  // warm from the sidebar / WS projection), so this adds no extra fetch in
  // the steady state.
  const accountsQuery = useAccounts();
  const account = accountsQuery.data?.find((a) => a.id === conversation?.account_id);

  return (
    <>
      <ChatHeader
        conversationId={conversationId}
        conversation={conversation}
        lock={effectiveLock}
        isLockedBySelf={isLockedBySelf}
      />
      <ConversationAccountBanner account={account} />
      <MessageList conversationId={conversationId} onReply={setReplyingTo} />
      <Composer
        conversationId={conversationId}
        replyingTo={replyingTo}
        onCancelReply={() => setReplyingTo(null)}
      />
    </>
  );
}

interface ChatHeaderProps {
  conversationId: string;
  conversation: Conversation | undefined;
  lock: Lock | undefined;
  isLockedBySelf: boolean;
}

function ChatHeader({
  conversationId,
  conversation,
  lock,
  isLockedBySelf,
}: ChatHeaderProps): React.JSX.Element {
  const profileOpen = usePrefsStore((s) => s.profileOpen);
  const toggleProfile = usePrefsStore((s) => s.toggleProfile);

  // Initials always prefer the display name (falling back as below) — kept
  // independent of the primary/secondary text below so a digits-only primary
  // line (the phone) never regresses the avatar to number initials.
  const contactName =
    conversation?.contact.display_name?.trim() ||
    conversation?.contact.phone_e164 ||
    conversation?.contact.peer_id ||
    '';
  const initials = contactName ? computeInitials(contactName) : '??';

  // PRIMARY line: the customer's phone number is the operational identifier
  // agents care about in this gaming-support console, so it takes precedence
  // over the WhatsApp display name; falls back to `contactName` (display
  // name, then peer-id, then the loading placeholder) when there's no phone.
  const hasPhone = !!conversation?.contact.phone_e164;
  const primaryText = hasPhone
    ? formatPhone(conversation?.contact.phone_e164)
    : contactName || 'Loading…';
  // SECONDARY line: the display name, shown only when the primary line is
  // showing the phone (there IS a phone) AND a non-blank display name also
  // exists — mirrors the old `showPhone` condition with the two roles
  // swapped; avoids duplicating the primary line or rendering an empty row.
  const showNameRow =
    hasPhone && !!conversation?.contact.display_name?.trim();

  return (
    // Neumorphic: the header floats above the message flow with a soft drop
    // shadow instead of a border (design §右栏 聊天头).
    <header className="relative z-[2] flex shrink-0 items-center gap-3 px-6 py-3 shadow-neu-header">
      <Avatar className="size-11 shadow-neu-flat">
        <AvatarFallback
          className={cn(
            'text-sm font-extrabold text-white',
            avatarGradientClass(conversation?.contact.id ?? conversationId),
          )}
        >
          {initials}
        </AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              'truncate text-[15.5px] font-extrabold',
              hasPhone && 'tabular-nums',
            )}
          >
            {primaryText}
          </span>
        </div>
        {showNameRow && (
          <span className="truncate text-xs font-semibold text-muted-foreground">
            {conversation?.contact.display_name?.trim()}
          </span>
        )}
      </div>
      {lock && (
        <span
          data-testid="chat-header-lock-pill"
          role="status"
          aria-label={
            isLockedBySelf
              ? 'Locked by you'
              : `Locked by ${lock.locked_by_display_name || 'another agent'}`
          }
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium leading-none',
            isLockedBySelf
              ? 'bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'
              : 'bg-amber-500/20 text-amber-700 dark:bg-amber-500/25 dark:text-amber-300',
          )}
        >
          <LockIcon className="size-3" />
          {isLockedBySelf ? 'You' : lock.locked_by_display_name || 'Other'}
        </span>
      )}
      {/* Customer-profile toggle — opens/closes the 4th-column sidebar. Reads
          inset/active (aria-pressed) while the sidebar is open. */}
      <Button
        type="button"
        size="icon"
        variant="ghost"
        aria-label="客户资料"
        aria-pressed={profileOpen}
        className={cn(
          'size-10 shrink-0 rounded-[13px]',
          profileOpen
            ? 'text-primary shadow-neu-inset-press'
            : 'shadow-neu-raised hover:shadow-neu-raised-hover active:shadow-neu-inset-press',
        )}
        onClick={toggleProfile}
      >
        <User className="size-4" />
      </Button>
    </header>
  );
}

function EmptyNoConversation(): React.JSX.Element {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center text-sm text-muted-foreground">
      <MessageSquare
        className="size-10 text-muted-foreground/60"
        aria-hidden="true"
      />
      <p className="font-medium text-foreground">No conversation selected</p>
      <p className="text-xs">Pick a conversation to start chatting.</p>
    </div>
  );
}

/** Same initials logic as ConversationRow — keep them in sync visually. */
function computeInitials(source: string): string {
  const trimmed = source.trim();
  if (!trimmed) return '??';
  const tokens = trimmed.split(/\s+/).filter(Boolean);
  if (tokens.length >= 2) {
    return (tokens[0][0] + tokens[1][0]).toUpperCase();
  }
  const single = tokens[0];
  return single.slice(0, 2).toUpperCase();
}
